import {
	IkeFieldType,
	IkeToken,
	IkeUnitDistance,
	type UserJSONObject,
	type UserJSONObjectWithTokenFlags,
} from '@ikegps/core-api';
import { IkeRequestError, IkeRequestErrorChecker, IkeRestUser } from '@ikegps/core-shared';
import { action, computed, observable, reaction, makeObservable } from 'mobx';
import { NotificationService } from '../components/notification.toast/notification.service';
import { T, Translate } from '../i18n';
import { type IkeClientService, Logger, Services } from './index';

const USER_STORAGE_KEY = 'currentUser';

type MaybePromise<K> = K | Promise<K>;

export abstract class UserService implements IkeClientService {
	readonly __priority = 25;

	abstract init(): void;

	@observable current: UserJSONObjectWithTokenFlags | UserJSONObject | null = null;
	@observable lastDepartmentId: string | null = null;

	constructor() {
		makeObservable(this);
	}

	@computed get hasUser(): boolean {
		return this.current !== null;
	}

	abstract get isAuthenticated(): boolean;

	public rest: IkeRestUser;

	abstract login(...args: any[]): MaybePromise<void>;

	abstract logout(): MaybePromise<void>;

	abstract load(user: UserJSONObject): MaybePromise<UserJSONObject>;

	abstract reloadCurrentUser(): MaybePromise<void>;

	isIkeAnalyst(currentJson: UserJSONObject | null): boolean {
		return !!currentJson?.email.endsWith('ikegps.com');
	}

	hasFormPermission(formOption: IkeFieldType): boolean {
		if (formOption === IkeFieldType.FILE_TOOL) {
			return Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE);
		}

		if (formOption === IkeFieldType.VERTICAL_SEPARATION) {
			return (
				Services.Token.has(IkeToken.FEATURE_FORM_VERTICAL_SEPARATION) ||
				Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE)
			);
		}

		return true;
	}

	@action protected setUser(user: UserJSONObject): UserJSONObject {
		this.current = user;
		if (user.settings != null) {
			this.lastDepartmentId = user.settings.departmentId;
		}
		return this.current;
	}
}

export class SSOUserService extends UserService {
	constructor() {
		super();

		makeObservable(this);
	}

	async init() {
		if (Services.SSO.isAuthenticated) {
			await this.refresh();
		} else {
			// This reaction MAY be redundant as the login will never happen
			// within this page since we redirect to Auth0 for login, and redirect back
			// once login is complete.
			reaction(
				() => Services.SSO.user,
				async () => {
					await this.refresh();
				}
			);
		}
	}

	async login() {
		await Services.SSO.login();
	}

	async logout(federated: boolean = false) {
		await Services.SSO.logout(federated);
	}

	reloadCurrentUser() {
		if (this.current != null) {
			this.load(this.current);
		}
	}

	load(user: UserJSONObject): UserJSONObject {
		if (Services.SSO.isAuthenticated) {
			this.rest = Services.Rest.rest.bind(Services.SSO.token);

			try {
				Services.Department.rest.updateBoundUser(this.rest);
			} catch (err) {
				Logger.error({ error: err }, 'Failed updating bound user');
				NotificationService.errorAsString('Fail to load user.');
			}
		}

		this.setUser(user);

		return this.current;
	}

	/**
	 * Check to see if the current user has changed
	 */
	@action private async refresh() {
		if (Services.SSO.isAuthenticated) {
			Logger.info(
				{
					currentUser: this.current && this.current.username,
					accessToken: Services.SSO.token,
				},
				'SSOUserProfile'
			);

			const { user, token } = Services.SSO;

			try {
				this.rest = await Services.Rest.rest.authenticate(user.sub, token);
			} catch (e) {
				if (IkeRequestError.isIkeRequestError(e) && IkeRequestErrorChecker.isValidationFailed(e)) {
					Logger.error(e.message);
					let msg = e.errors[0]?.message || Translate(T.ERROR_GENERAL);
					NotificationService.errorAsString(msg);
				} else {
					Services.Log.Logger.error(e);
					Logger.error(T.ERROR_GENERAL);
					NotificationService.error({ key: T.ERROR_GENERAL });
				}

				//we have failed to authenticate user and thrown some notifications
				//Clear out set info, logout, go back to login page.
				this.rest = null;
				if (this.hasUser) {
					const userKey = Services.LocalStorage.storageKey(USER_STORAGE_KEY);
					Services.LocalStorage.removeItem(userKey);
				}
				// eslint-disable-next-line @typescript-eslint/no-floating-promises
				this.logout(true); //logout from SSO
			}

			if (this.rest && token) {
				const profile = await this.rest.User.me();
				this.rest.me.init(profile);

				this.setUser(profile);
			}
		}
	}

	@computed get isAuthenticated() {
		return Services.SSO.isAuthenticated;
	}
}

export class UserMutator {
	static async setUnits(units: IkeUnitDistance): Promise<UserJSONObject> {
		Services.Analytics.trackEvent(Services.Analytics.EventCategory.Mutate, 'User_setSettings.units', units, 1);

		const user = await Services.User.rest.User.Settings.change({ units });
		return Services.User.load(user);
	}

	static async setDateFormat(dateFormat: string): Promise<UserJSONObject> {
		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.Mutate,
			'User_setSettings.dateFormat',
			dateFormat,
			1
		);

		const user = await Services.User.rest.User.Settings.change({ dateFormat });
		return Services.User.load(user);
	}

	static async setDepartment(departmentId: string): Promise<UserJSONObject> {
		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.Mutate,
			'User_setSettings.departmentId',
			departmentId,
			1
		);

		const user = await Services.User.rest.User.Settings.change({ departmentId });
		return Services.User.load(user);
	}

	static async setLastViewedReleaseNotesVersion(version: string): Promise<UserJSONObject> {
		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.Mutate,
			'User_setSettings.lastViewedReleaseNotesVersion',
			version,
			1
		);

		const user = await Services.User.rest.User.Settings.change({ lastViewedReleaseNotesVersion: version });
		return Services.User.load(user);
	}

	static async requestPasswordReset(email: string): Promise<boolean> {
		Services.Analytics.trackEvent(Services.Analytics.EventCategory.Mutate, 'User_requestPasswordReset', email, 1);

		try {
			await Services.Rest.rest.SSOUser.triggerResetPassword({ email });
			return true;
		} catch (_e) {
			return false;
		}
	}
}
